/**
 * Collateral Monitoring API Service
 *
 * Type-safe collateral monitoring dashboard endpoints using openapi-fetch.
 * Types derived from platform-admin.openapi.yaml -- no manual interfaces.
 *
 * GH #2917 deliverable 2. The three list readers returned `data.points ?? []`,
 * `data.distribution ?? []` and `data.deficiencies ?? []`. Every one of those
 * lists is OPTIONAL in the canonical spec (schema-registry/platform-admin.openapi.yaml,
 * the value-trend, distribution and deficiencies responses declare no required
 * fields), so a 200 that omits its list is contract-valid and says nothing about
 * what the institution holds. The `?? []` turned that silence into a measured
 * empty list, and the page then printed a measured-empty sentence for a list it
 * never received. That is the success-branch coercion `.claude/rules/no-fabricated-values.md`
 * forbids, and it is the shape GH #2742 already removed from the dashboard tiles.
 *
 * The body now reaches the caller exactly as it was read. An absent list arrives
 * as `undefined` and the page decides what that means; an empty array arrives as
 * an empty array and stays a measurement.
 */

import { platformAdminClient } from '../client';
import type { components } from '@aaimplatform/institutional-types/platform-admin';

export type CollateralMonitoringSummary = components['schemas']['CollateralMonitoringSummary'];
export type CollateralValuePoint = components['schemas']['CollateralValuePoint'];
export type AssetDistribution = components['schemas']['AssetDistribution'];
export type CollateralDeficiency = components['schemas']['CollateralDeficiency'];

export const collateralMonitoringApi = {
  getSummary: async (): Promise<CollateralMonitoringSummary> => {
    const { data, error } = await platformAdminClient.GET('/collateral/monitoring/summary');
    if (error) throw new Error('Failed to fetch collateral monitoring summary');
    return data;
  },

  getValueTrend: async (hours?: number) => {
    const { data, error } = await platformAdminClient.GET('/collateral/monitoring/value-trend', {
      params: { query: { hours } },
    });
    if (error) throw new Error('Failed to fetch collateral value trend');
    return data;
  },

  getDistribution: async () => {
    const { data, error } = await platformAdminClient.GET('/collateral/monitoring/distribution');
    if (error) throw new Error('Failed to fetch collateral distribution');
    return data;
  },

  getDeficiencies: async () => {
    const { data, error } = await platformAdminClient.GET('/collateral/monitoring/deficiencies');
    if (error) throw new Error('Failed to fetch collateral deficiencies');
    return data;
  },
};
